import { Link } from 'react-router-dom';
import { Navbar } from '../home/components/Navbar';
import { Footer } from '../home/components/Footer';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const values = [
  {
    icon: 'ri-medal-line',
    title: 'Uncompromising Quality',
    description: 'We only use industry-leading film brands — 3M, LLumar, XPEL — and every installation is inspected to our strict zero-defect standard before delivery.',
  },
  {
    icon: 'ri-user-heart-line',
    title: 'Client-First Service',
    description: 'From your first call to your final walkthrough, our team is transparent, communicative, and committed to exceeding your expectations at every step.',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Certified Expertise',
    description: 'All technicians hold professional certifications and undergo continuous training on new materials and installation techniques — no shortcuts, ever.',
  },
  {
    icon: 'ri-leaf-line',
    title: 'Built for Florida',
    description: 'South Florida\'s intense heat and salt air demands specialized solutions. Our film selections are engineered specifically for the harshest UV and coastal environments.',
  },
];

const team = [
  {
    name: 'Michael R.',
    role: 'Lead Installer & Founder',
    image: 'https://public.readdy.ai/ai/img_res/90a1ee62ab05a093c99dd02b19005d49.jpg',
    bio: '15+ years of precision tinting. Founded Mr. Tint with one mission: make South Florida\'s premium tinting accessible without compromising on quality.',
  },
  {
    name: 'Carlos M.',
    role: 'Senior Automotive Technician',
    image: 'https://readdy.ai/api/search-image?query=professional%20confident%20hispanic%20male%20automotive%20technician%20portrait%20studio%20background%20clean%20professional%20headshot%20mid%2030s%20skilled%20tradesman&width=400&height=400&seq=team002&orientation=squarish',
    bio: 'Certified in XPEL and 3M installations. Carlos specializes in exotic and luxury vehicles, delivering flawless results on the most demanding projects.',
  },
  {
    name: 'Tyler B.',
    role: 'Residential & Marine Specialist',
    image: 'https://readdy.ai/api/search-image?query=professional%20confident%20young%20male%20skilled%20tradesman%20portrait%20studio%20clean%20background%20professional%20headshot%20late%2020s%20home%20service%20professional&width=400&height=400&seq=team003&orientation=squarish',
    bio: 'Tyler brings an architect\'s eye for detail to every residential and marine install, ensuring precise cuts, clean edges, and perfect adhesion.',
  },
];

const milestones = [
  { year: '2009', event: 'Founded in Palm City, FL with a single bay and a commitment to precision.' },
  { year: '2012', event: 'Became an authorized 3M and LLumar dealer — a recognition of consistent quality.' },
  { year: '2016', event: 'Expanded to residential and marine window film services.' },
  { year: '2019', event: 'Added XPEL Paint Protection Film and Ceramic Coating services.' },
  { year: '2022', event: 'Surpassed 5,000 completed installations across South Florida.' },
  { year: '2026', event: 'Recognized as Martin County\'s #1 rated window tinting company.' },
];

function SectionHeader({ label, title, subtitle }: { label: string; title: React.ReactNode; subtitle?: string }) {
  const { ref, isVisible } = useScrollAnimation();
  return (
    <div ref={ref} className={`anim-hidden ${isVisible ? 'animate-fade-in-up' : ''} text-center mb-16`}>
      <span className="luxury-divider mx-auto" />
      <span className="text-xs font-bold uppercase tracking-[5px] text-[#C9A96E] mb-4 block">{label}</span>
      <h2 className="text-4xl sm:text-5xl font-bold text-white leading-tight tracking-tight" style={{ fontFamily: 'Sora, sans-serif' }}>
        {title}
      </h2>
      {subtitle && <p className="text-white/40 text-sm mt-4 max-w-xl mx-auto leading-relaxed">{subtitle}</p>}
    </div>
  );
}

export default function AboutPage() {
  const heroRef = useScrollAnimation();
  const valuesRef = useScrollAnimation();
  const teamRef = useScrollAnimation();
  const timelineRef = useScrollAnimation();

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />

      {/* Page Hero */}
      <section className="pt-40 pb-24 relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=professional%20auto%20detailing%20shop%20interior%20luxury%20service%20center%20team%20working%20cinematic%20lighting%20dark%20atmosphere%20precision%20craftsmanship%20clean%20modern%20shop%20floor%20premium%20automotive%20service&width=1920&height=700&seq=about_hero_01&orientation=landscape"
            alt="About Mr. Tint"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/70 via-[#0A0A0A]/60 to-[#0A0A0A]" />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10" ref={heroRef.ref}>
          <div className={`anim-hidden ${heroRef.isVisible ? 'animate-fade-in-up' : ''} max-w-3xl`}>
            <div className="flex items-center gap-3 mb-6">
              <Link to="/" className="text-white/40 hover:text-white text-sm transition-colors cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line text-white/20 text-sm" />
              <span className="text-[#C9A96E] text-sm">About Us</span>
            </div>
            <span className="luxury-divider" />
            <span className="text-xs font-bold uppercase tracking-[5px] text-[#C9A96E] mb-4 block">Our Story</span>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-tight tracking-tight mb-6" style={{ fontFamily: 'Sora, sans-serif' }}>
              South Florida&rsquo;s<br /><span className="shimmer-gold">Tinting Authority</span>
            </h1>
            <p className="text-white/50 text-lg leading-relaxed mb-10 max-w-xl">
              Since 2009, Mr. Tint of America has been the trusted name for precision window tinting, paint protection, and ceramic coating across Palm Beach and Martin County.
            </p>
            <div className="flex flex-wrap gap-8">
              {[{ value: '15+', label: 'Years in Business' }, { value: '5,000+', label: 'Installations' }, { value: '500+', label: '5-Star Reviews' }, { value: '100%', label: 'Satisfaction Guarantee' }].map((stat) => (
                <div key={stat.label}>
                  <div className="text-3xl font-bold shimmer-gold" style={{ fontFamily: 'Sora, sans-serif' }}>{stat.value}</div>
                  <div className="text-xs text-white/40 mt-0.5 uppercase tracking-wider">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Our Mission */}
      <section className="py-24 bg-[#0D0D0D]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div ref={valuesRef.ref} className={`anim-hidden ${valuesRef.isVisible ? 'animate-fade-in-up' : ''}`}>
              <span className="luxury-divider" />
              <span className="text-xs font-bold uppercase tracking-[5px] text-[#C9A96E] mb-4 block">Our Mission</span>
              <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6 leading-tight tracking-tight" style={{ fontFamily: 'Sora, sans-serif' }}>
                Precision That<br /><span className="text-[#FF6B35]">Never Compromises</span>
              </h2>
              <p className="text-white/50 text-base leading-relaxed mb-6">
                We started with a single bay and a clear belief: that window tinting done right is an art form. Every film matters. Every edge counts. Every customer deserves the best materials, installed with care.
              </p>
              <p className="text-white/50 text-base leading-relaxed">
                Today, Mr. Tint of America is the premier tinting destination for vehicle owners, homeowners, and boat owners across South Florida — trusted by thousands, backed by the industry&rsquo;s top brands.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {values.map((v, i) => (
                <div
                  key={i}
                  className={`anim-hidden ${valuesRef.isVisible ? `animate-fade-in-up delay-${(i + 1) * 100}` : ''} bg-[#111111] border border-white/5 hover:border-[#C9A96E]/30 rounded-2xl p-6 transition-all duration-300 cursor-default`}
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-[#FF6B35]/10 rounded-xl mb-4">
                    <i className={`${v.icon} text-lg text-[#FF6B35]`} />
                  </div>
                  <h3 className="text-sm font-bold text-white mb-2 tracking-tight" style={{ fontFamily: 'Sora, sans-serif' }}>{v.title}</h3>
                  <p className="text-white/40 text-xs leading-relaxed">{v.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <SectionHeader label="Our Team" title={<>The People Behind<br /><span className="shimmer-gold">Every Perfect Install</span></>} subtitle="Certified professionals who treat every vehicle and home with the same care they'd want for their own." />
          <div ref={teamRef.ref} className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {team.map((member, i) => (
              <div
                key={i}
                className={`anim-hidden ${teamRef.isVisible ? `animate-fade-in-up delay-${(i + 1) * 100}` : ''} bg-[#111111] border border-white/5 hover:border-[#C9A96E]/30 rounded-3xl overflow-hidden transition-all duration-300 group cursor-default`}
              >
                <div className="relative h-64 overflow-hidden">
                  <img src={member.image} alt={member.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#111111] via-transparent to-transparent" />
                </div>
                <div className="p-6">
                  <h3 className="text-base font-bold text-white mb-0.5 tracking-tight" style={{ fontFamily: 'Sora, sans-serif' }}>{member.name}</h3>
                  <p className="text-[#C9A96E] text-xs font-medium uppercase tracking-wider mb-3">{member.role}</p>
                  <p className="text-white/40 text-xs leading-relaxed">{member.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 bg-[#0D0D0D]">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <SectionHeader label="Our History" title={<>15 Years of<br /><span className="shimmer-gold">Earned Excellence</span></>} />
          <div ref={timelineRef.ref} className="relative">
            <div className="absolute left-6 sm:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-[#C9A96E]/30 via-[#FF6B35]/20 to-transparent" />
            <div className="space-y-10">
              {milestones.map((m, i) => (
                <div
                  key={i}
                  className={`anim-hidden ${timelineRef.isVisible ? `animate-fade-in-up delay-${(i + 1) * 100}` : ''} relative flex flex-col sm:flex-row gap-6 ${i % 2 === 0 ? 'sm:pr-1/2 sm:text-right sm:flex-row-reverse sm:items-start' : 'sm:pl-1/2 sm:items-start'}`}
                >
                  <div className="absolute left-6 sm:left-1/2 w-3 h-3 rounded-full bg-[#FF6B35] -translate-x-1/2 mt-2 ring-4 ring-[#0D0D0D]" />
                  <div className="pl-12 sm:pl-0 sm:w-[calc(50%-2rem)] bg-[#111111] border border-white/5 hover:border-[#C9A96E]/20 rounded-2xl p-6 transition-all">
                    <span className="text-[#C9A96E] text-2xl font-bold" style={{ fontFamily: 'Sora, sans-serif' }}>{m.year}</span>
                    <p className="text-white/50 text-sm mt-2 leading-relaxed">{m.event}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4 tracking-tight" style={{ fontFamily: 'Sora, sans-serif' }}>
            Experience the <span className="shimmer-gold">Mr. Tint Difference</span>
          </h2>
          <p className="text-white/40 mb-10 text-base">Free consultations · No-pressure quotes · Same-day service available</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/#quote"
              className="bg-[#FF6B35] hover:bg-[#E55A2B] text-white px-8 py-4 rounded-full text-sm font-bold tracking-wide transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              Get a Free Quote
            </Link>
            <Link
              to="/services"
              className="border border-white/20 hover:border-white/40 text-white px-8 py-4 rounded-full text-sm font-semibold transition-all hover:bg-white/5 whitespace-nowrap cursor-pointer"
            >
              View Our Services
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
